import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowDown, ArrowRight } from "lucide-react";
import EmberCanvas from "./EmberCanvas";
import { useMetals, fmtMoney } from "../hooks/useMetals";
import { useLang } from "../i18n";
import { scrollToId } from "./Navbar";

export default function Hero() {
  const metals = useMetals();
  const { lang, L } = useLang();
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const glow = useTransform(scrollYProgress, [0, 1], [0.6, 0]);

  const gold18 = metals?.prices?.find((p) => p.symbol === "XAU");
  const lines = lang === "it" ? ["La tua idea,", "fusa in Italia."] : ["Your idea,", "cast in Italy."];

  const stats = [
    ["1,200+", { en: "pieces cast", it: "pezzi fusi" }],
    ["5", { en: "crafts, one roof", it: "mestieri, un solo atelier" }],
    ["60 s", { en: "spot refresh", it: "aggiornamento spot" }],
  ];

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <EmberCanvas />
      <motion.div
        style={{ opacity: glow }}
        className="pointer-events-none absolute left-1/2 top-[-20%] h-[60vh] w-[80vw] -translate-x-1/2 rounded-full bg-[#C9A84C]/15 blur-[120px]"
      />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-6 pb-28 pt-20 lg:grid-cols-12 lg:pt-28">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mono-tech mb-8 text-[#A8853B]"
            data-testid="hero-eyebrow"
          >
            {L({ en: "Custom Jewellery Manufacturing — Urbino, Italy", it: "Produzione Orafa su Misura — Urbino, Italia" })}
          </motion.p>
          <h1 className="font-display text-5xl leading-[1.05] tracking-tight text-[#0E3B33] sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            {lines.map((line, i) => (
              <span key={lang + i} className="mask-line">
                <motion.span
                  className={`block ${i === 1 ? "gold-text italic" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.0, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[#4A5248] sm:text-lg"
          >
            {L({
              en: "An artisan jewellery casting lab in Urbino, Central Italy — lost-wax casting, CAD design and hand finishing for independent designers, jewellery brands and retail partners worldwide. Every estimate is priced against today's live metal spot rate.",
              it: "Un laboratorio artigianale di fusione a Urbino, Italia Centrale — fusione a cera persa, design CAD e finitura a mano per designer indipendenti, brand di gioielleria e partner retail in tutto il mondo. Ogni preventivo è calcolato sulla quotazione live del metallo.",
            })}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button data-testid="hero-estimate-btn" onClick={() => scrollToId("#calculator")} className="ember-btn">
              {L({ en: "Estimate a Piece", it: "Stima il tuo Pezzo" })} <ArrowRight className="h-4 w-4" />
            </button>
            <button data-testid="hero-start-project-btn" onClick={() => navigate("/contact")} className="ghost-btn">
              {L({ en: "Start a Project", it: "Inizia un Progetto" })}
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-16 grid max-w-lg grid-cols-3 gap-6 border-t hairline pt-8"
          >
            {stats.map(([v, l], i) => (
              <div key={i} data-testid={`hero-stat-${i}`}>
                <div className="font-display text-2xl text-[#0E3B33] sm:text-3xl">{v}</div>
                <div className="mono-tech mt-1.5 text-[#8A8F82]">{L(l)}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden rounded-sm border border-[#C9A84C]/40"
            style={{ clipPath: "polygon(0 0, 100% 0, 100% 92%, 88% 100%, 0 100%)" }}
          >
            <motion.img
              style={{ y: imgY }}
              src="https://images.unsplash.com/photo-1777107508993-7c254c95668c?q=80&w=1200&auto=format&fit=crop"
              alt="Goldsmith working with torch at the bench"
              className="h-[520px] w-full scale-110 object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0E3B33]/30 via-transparent to-transparent" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            data-testid="hero-live-rate-card"
            className="absolute -bottom-8 -left-4 rounded-lg border border-[#C9A84C]/40 bg-white/90 p-5 shadow-xl backdrop-blur-xl sm:-left-10"
          >
            <div className="mono-tech text-[#8A8F82]">{L({ en: "Live atelier rate — 18K gold", it: "Tariffa live atelier — oro 18K" })}</div>
            <div className="mt-2 font-display text-3xl text-[#A8853B]" data-testid="hero-live-rate-value">
              {gold18 ? `${fmtMoney(gold18.eur_per_gram * 0.75)}/g` : "···"}
            </div>
            <div className="mt-1 text-xs text-[#8A8F82]">
              {metals
                ? metals.stale
                  ? L({ en: "Delayed spot · gold-api.com", it: "Spot differito · gold-api.com" })
                  : L({ en: "Live spot · gold-api.com", it: "Spot live · gold-api.com" })
                : L({ en: "Fetching market…", it: "Connessione al mercato…" })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
